import { derivePrimitiveCameraFocus } from '../cameraFocus';
import { projectDecidedNaturalComparison, projectEmbeddedNaturalComparison } from './embeddedComparison';
import { momentKey, momentPart } from '../primitiveMoment';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '../../../_contract/_~generated/operationDirectionEffect';
import type { Config, PrimitiveWheelState } from '../../../_contract/config';
import type { PrimitiveKeyframe, PrimitiveStepState } from '../../../_contract/steps';
import type { NaturalDivisionTrace } from '../../../_contract/trace';

/** Check after initialization and each completed Quotient increment, never between removal and counting. */
export function withDivisionComparisons(args: {
    config: Config;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    steps: readonly PrimitiveStepState[];
    trace: NaturalDivisionTrace;
    /** Comparisons after this index flip Decision in place; omitted keeps every comparison full. */
    fullComparisons?: number;
}): readonly PrimitiveStepState[] {
    const { config, trace, steps } = args;
    const result: PrimitiveStepState[] = [];
    let decision: PrimitiveWheelState | undefined;
    let channel: PrimitiveKeyframe['booleanRod'];
    const dim = (wheels: readonly PrimitiveWheelState[]) => wheels.map(wheel =>
        wheel.role === 'divisor' && wheel.variant === undefined ? { ...wheel,
            bandOpacityByPower: Object.fromEntries(wheel.powers.map(power => [power, config.natural.display.band.spentOpacity])),
        } : wheel);
    const decorate = (frame: PrimitiveKeyframe): PrimitiveKeyframe => ({
        ...frame, wheels: [...frame.wheels, ...(decision ? [decision] : [])],
        ...(channel ? { booleanRod: channel } : {}),
    });
    const compare = (index: number, remainder: number, wheels: readonly PrimitiveWheelState[]) => {
        const options = {
            config, directionCatalog: args.directionCatalog,
            source: remainder, target: trace.divisor, sourceRole: 'remainder' as const, targetRole: 'divisor' as const,
            resting: dim(wheels), frame: momentPart('division-comparison', undefined, index), view: 'division-comparison-integrated',
        };
        const comparison = (args.fullComparisons === undefined || index <= args.fullComparisons
            ? projectEmbeddedNaturalComparison(options)
            : [projectDecidedNaturalComparison({ ...options, previous: decision, cameraFocus: { stationKey: 'overview',
                viewKey: 'overview', roles: ['dividend', 'divisor', 'remainder', 'quotient', 'less-than-decision'] } })]
        ).map(step => ({ ...step, keyframes: step.keyframes.map(frame => ({
            ...frame, booleanRod: frame.booleanRod && { ...frame.booleanRod, preserveNaturalAxis: true },
        })) }));
        result.push(...comparison);
        const returned = comparison.at(-1)!.keyframes.at(-1)!;
        decision = returned.wheels.find(wheel => wheel.role === 'less-than-decision')!;
        channel = returned.booleanRod;
        const stop = remainder < trace.divisor;
        result.push({
            ...momentKey({ frames: [momentPart('division-comparison', undefined, index)], step: momentPart('gate') }),
            label: stop ? 'Finish Division' : 'Enable Divisor',
            description: stop ? 'Decision is true: no whole Divisor remains. Keep Divisor dimmed.'
                : 'Decision is false: enable Divisor for another whole removal.',
            cameraFocus: { stationKey: 'overview', viewKey: 'overview',
                roles: ['dividend', 'divisor', 'remainder', 'quotient', 'less-than-decision'] },
            keyframes: [
                { ...returned, transition: 'instant' },
                { ...returned, wheels: returned.wheels.map(wheel => wheel.role === 'divisor' && !stop
                    ? { ...wheel, bandOpacityByPower: undefined } : wheel),
                    transition: { durationMs: config.base.animation.fadeDurationMs ?? 0 }, events: [] },
            ],
        });
    };
    let previous: readonly PrimitiveWheelState[] = [];
    for (const [index, step] of steps.entries()) {
        const frame = step.moment?.frames[0];
        const initializing = frame?.kind === 'initialization';
        const incrementing = frame?.kind === 'quotient-increment';
        const focus = derivePrimitiveCameraFocus(step, previous);
        const mapped = { ...step, cameraFocus: focus, keyframes: step.keyframes.map(frame => decorate({
            ...frame, wheels: initializing || incrementing ? dim(frame.wheels) : frame.wheels,
        })) };
        result.push(mapped);
        previous = step.keyframes.at(-1)!.wheels;
        const resting = mapped.keyframes.at(-1)!.wheels.filter(wheel => wheel.role !== 'less-than-decision');
        const next = steps[index + 1];
        if (initializing && next?.moment?.frames[0]?.kind !== 'initialization') compare(1, trace.dividend, resting);
        if (incrementing) {
            const cycle = trace.cycles.find(cycle => frame.ordinal?.index === cycle.cycleIndex - 1);
            if (!cycle) throw new Error('Quotient settlement has no division cycle.');
            const nextFrame = next?.moment?.frames[0];
            if (nextFrame?.kind !== 'quotient-increment' || nextFrame.ordinal?.index !== cycle.cycleIndex - 1) {
                compare(cycle.cycleIndex + 1, cycle.remainderAfter, resting);
            }
        }
    }
    return result;
}
